/**
 * Shared mesh operations for exported CAD modules.
 * Self-contained — only depends on Three.js.
 */
import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { Font } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

export const prepare = (geo) => {
    let g = geo.index ? geo.toNonIndexed() : geo.clone();
    for (const key of Object.keys(g.attributes)) {
        if (key !== 'position') g.deleteAttribute(key);
    }
    g = mergeVertices(g);
    g.computeVertexNormals();
    return g;
};

export const clean = (geo) => {
    let g = geo.index ? geo : geo.toNonIndexed();
    g = mergeVertices(g, 1e-4);
    g.computeVertexNormals();
    g.computeBoundingBox();
    return g;
};

/* ================================================================== */
/*  Overlay / Delete / Remove                                          */
/* ================================================================== */

export function buildOverlayFromFaces(geo, faceIndices) {
    const pos = geo.getAttribute('position');
    const idx = geo.getIndex();
    if (faceIndices.size === 0) return new THREE.BufferGeometry();

    const positions = [];
    for (const f of faceIndices) {
        let i0, i1, i2;
        if (idx) {
            i0 = idx.getX(f * 3);
            i1 = idx.getX(f * 3 + 1);
            i2 = idx.getX(f * 3 + 2);
        } else {
            i0 = f * 3; i1 = f * 3 + 1; i2 = f * 3 + 2;
        }
        positions.push(
            pos.getX(i0), pos.getY(i0), pos.getZ(i0),
            pos.getX(i1), pos.getY(i1), pos.getZ(i1),
            pos.getX(i2), pos.getY(i2), pos.getZ(i2),
        );
    }

    const result = new THREE.BufferGeometry();
    result.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    result.computeVertexNormals();
    return result;
}

export function deleteFaces(geo, faceIndices) {
    const pos = geo.getAttribute('position');
    const idx = geo.getIndex();
    const fCount = idx ? idx.count / 3 : pos.count / 3;

    const keptFaces = [];
    for (let f = 0; f < fCount; f++) {
        if (faceIndices.has(f)) continue;
        if (idx) {
            keptFaces.push(idx.getX(f * 3), idx.getX(f * 3 + 1), idx.getX(f * 3 + 2));
        } else {
            keptFaces.push(f * 3, f * 3 + 1, f * 3 + 2);
        }
    }

    let result = new THREE.BufferGeometry();
    result.setAttribute('position', pos.clone());
    result.setIndex(keptFaces);
    result = mergeVertices(result, 1e-4);
    result.computeVertexNormals();
    result.computeBoundingBox();
    return result;
}

/* ================================================================== */
/*  Boundary loops                                                     */
/* ================================================================== */

export function findBoundaryLoops(geo) {
    const idx = geo.getIndex();
    if (!idx) return [];
    const faceCount = idx.count / 3;

    const allHE = new Set();
    const edgeList = [];

    for (let f = 0; f < faceCount; f++) {
        const i0 = idx.getX(f * 3);
        const i1 = idx.getX(f * 3 + 1);
        const i2 = idx.getX(f * 3 + 2);
        for (const [a, b] of [[i0, i1], [i1, i2], [i2, i0]]) {
            allHE.add(`${a}:${b}`);
            edgeList.push([a, b]);
        }
    }

    const boundaryNext = new Map();
    for (const [a, b] of edgeList) {
        if (!allHE.has(`${b}:${a}`)) {
            boundaryNext.set(a, b);
        }
    }

    const visited = new Set();
    const loops = [];
    for (const start of boundaryNext.keys()) {
        if (visited.has(start)) continue;
        const loop = [];
        let cur = start;
        for (let safety = 0; safety < 1_000_000; safety++) {
            if (visited.has(cur)) break;
            visited.add(cur);
            loop.push(cur);
            const next = boundaryNext.get(cur);
            if (next === undefined) break;
            cur = next;
        }
        if (loop.length >= 3) loops.push(loop);
    }
    return loops;
}

/* ================================================================== */
/*  Consistent normals                                                 */
/* ================================================================== */

export function ensureConsistentNormals(geo) {
    const idx = geo.getIndex();
    if (!idx) return geo;

    const faceCount = idx.count / 3;
    const indices = new Uint32Array(idx.array);

    const edgeToFaces = new Map();
    for (let f = 0; f < faceCount; f++) {
        const verts = [indices[f * 3], indices[f * 3 + 1], indices[f * 3 + 2]];
        for (let e = 0; e < 3; e++) {
            const a = verts[e], b = verts[(e + 1) % 3];
            const key = Math.min(a, b) + ':' + Math.max(a, b);
            if (!edgeToFaces.has(key)) edgeToFaces.set(key, []);
            edgeToFaces.get(key).push({ face: f, v0: a, v1: b });
        }
    }

    const visited = new Uint8Array(faceCount);
    for (let seed = 0; seed < faceCount; seed++) {
        if (visited[seed]) continue;
        const queue = [seed];
        visited[seed] = 1;

        while (queue.length > 0) {
            const f = queue.shift();
            const fv = [indices[f * 3], indices[f * 3 + 1], indices[f * 3 + 2]];

            for (let e = 0; e < 3; e++) {
                const a = fv[e], b = fv[(e + 1) % 3];
                const key = Math.min(a, b) + ':' + Math.max(a, b);
                const entries = edgeToFaces.get(key);
                if (!entries) continue;

                for (const nb of entries) {
                    if (nb.face === f || visited[nb.face]) continue;
                    visited[nb.face] = 1;

                    if (nb.v0 === a && nb.v1 === b) {
                        const base = nb.face * 3;
                        const tmp = indices[base];
                        indices[base] = indices[base + 2];
                        indices[base + 2] = tmp;
                    }
                    queue.push(nb.face);
                }
            }
        }
    }

    geo.setIndex(new THREE.BufferAttribute(indices, 1));

    const pos = geo.getAttribute('position');
    let vol6 = 0;
    for (let f = 0; f < faceCount; f++) {
        const ia = indices[f * 3], ib = indices[f * 3 + 1], ic = indices[f * 3 + 2];
        const ax = pos.getX(ia), ay = pos.getY(ia), az = pos.getZ(ia);
        const bx = pos.getX(ib), by = pos.getY(ib), bz = pos.getZ(ib);
        const cx = pos.getX(ic), cy = pos.getY(ic), cz = pos.getZ(ic);
        vol6 += ax * (by * cz - bz * cy) + bx * (cy * az - cz * ay) + cx * (ay * bz - az * by);
    }
    if (vol6 < 0) {
        for (let f = 0; f < faceCount; f++) {
            const base = f * 3;
            const tmp = indices[base];
            indices[base] = indices[base + 2];
            indices[base + 2] = tmp;
        }
        geo.setIndex(new THREE.BufferAttribute(indices, 1));
    }

    geo.computeVertexNormals();
    return geo;
}

/* ================================================================== */
/*  Smooth normals                                                     */
/* ================================================================== */

export function smoothNormals(pos, nor, idx, iterations = 2) {
    const vCount = pos.count;
    const fCount = idx.count / 3;

    const adj = Array.from({ length: vCount }, () => new Set());
    for (let f = 0; f < fCount; f++) {
        const a = idx.getX(f * 3), b = idx.getX(f * 3 + 1), c = idx.getX(f * 3 + 2);
        adj[a].add(b); adj[a].add(c);
        adj[b].add(a); adj[b].add(c);
        adj[c].add(a); adj[c].add(b);
    }

    let cur = new Float32Array(nor.array);
    const tmp = new Float32Array(vCount * 3);

    for (let it = 0; it < iterations; it++) {
        for (let v = 0; v < vCount; v++) {
            let nx = cur[v * 3], ny = cur[v * 3 + 1], nz = cur[v * 3 + 2];
            let count = 1;
            for (const nb of adj[v]) {
                nx += cur[nb * 3];
                ny += cur[nb * 3 + 1];
                nz += cur[nb * 3 + 2];
                count++;
            }
            const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
            tmp[v * 3]     = nx / len;
            tmp[v * 3 + 1] = ny / len;
            tmp[v * 3 + 2] = nz / len;
        }
        cur = new Float32Array(tmp);
    }
    return cur;
}

/* ================================================================== */
/*  Font cache                                                         */
/* ================================================================== */

let _fontCache = null;
export async function preloadFont() {
    if (_fontCache) return;
    // Use dynamic fetch to load the font file from three.js package
    try {
        const data = (await import('three/examples/fonts/helvetiker_bold.typeface.json')).default;
        _fontCache = new Font(data);
    } catch (e) {
        console.warn('Could not load helvetiker font, text engraving will be skipped:', e);
    }
}
export function getCachedFont() { return _fontCache; }

/* ================================================================== */
/*  Model Maker                                                        */
/* ================================================================== */

export function modelMaker(geo, opts = {}) {
    const {
        baseHeight = 2,
        thickness = 0.5,
        border = 2,
        text = '',
    } = opts;

    const BORDER = border;
    const borderWidth = thickness;

    const src = prepare(geo);
    const loops = findBoundaryLoops(src);
    if (loops.length === 0) return clean(src);

    src.computeBoundingBox();
    const origIdx = src.getIndex();
    const origPos = src.getAttribute('position');
    const oVCount = origPos.count;

    const smoothed = smoothNormals(origPos, src.getAttribute('normal'), origIdx, 3);

    const origLoops = loops;

    const bboxCenter = new THREE.Vector3();
    src.boundingBox.getCenter(bboxCenter);

    let avgBoundaryY = 0, boundaryTotal = 0;
    for (const loop of origLoops) {
        for (const vi of loop) { avgBoundaryY += origPos.getY(vi); boundaryTotal++; }
    }
    avgBoundaryY /= (boundaryTotal || 1);
    const wallDir = avgBoundaryY >= bboxCenter.y ? 1 : -1;

    let edgeY = wallDir > 0 ? -Infinity : Infinity;
    for (const loop of origLoops) {
        for (const vi of loop) {
            const y = origPos.getY(vi);
            if (wallDir > 0) { if (y > edgeY) edgeY = y; }
            else             { if (y < edgeY) edgeY = y; }
        }
    }
    const topY = edgeY + baseHeight * wallDir;

    const positions = [];
    for (let i = 0; i < oVCount; i++) {
        positions.push(origPos.getX(i), origPos.getY(i), origPos.getZ(i));
    }
    for (let i = 0; i < oVCount; i++) {
        positions.push(
            origPos.getX(i) - smoothed[i * 3]     * thickness,
            origPos.getY(i) - smoothed[i * 3 + 1] * thickness,
            origPos.getZ(i) - smoothed[i * 3 + 2] * thickness,
        );
    }

    const fCount = origIdx.count / 3;
    const faces = [];
    for (let f = 0; f < fCount; f++) {
        const i0 = origIdx.getX(f * 3), i1 = origIdx.getX(f * 3 + 1), i2 = origIdx.getX(f * 3 + 2);
        faces.push(i0, i1, i2);
        faces.push(i0 + oVCount, i2 + oVCount, i1 + oVCount);
    }

    const addVert = (x, y, z) => {
        const idx = positions.length / 3;
        positions.push(x, y, z);
        return idx;
    };

    for (let li = 0; li < origLoops.length; li++) {
        const loop = origLoops[li];
        const n = loop.length;
        if (n < 3) continue;

        const rawX = new Float64Array(n);
        const rawZ = new Float64Array(n);
        for (let i = 0; i < n; i++) {
            rawX[i] = origPos.getX(loop[i]);
            rawZ[i] = origPos.getZ(loop[i]);
        }

        const bx = Float64Array.from(rawX);
        const bz = Float64Array.from(rawZ);
        for (let iter = 0; iter < 100; iter++) {
            const tmpX = Float64Array.from(bx);
            const tmpZ = Float64Array.from(bz);
            for (let i = 0; i < n; i++) {
                const prev = (i - 1 + n) % n;
                const next = (i + 1) % n;
                bx[i] = (tmpX[prev] + tmpX[i] + tmpX[next]) / 3;
                bz[i] = (tmpZ[prev] + tmpZ[i] + tmpZ[next]) / 3;
            }
        }
        const sx = bx;
        const sz = bz;

        let cx = 0, cz = 0;
        for (let i = 0; i < n; i++) { cx += sx[i]; cz += sz[i]; }
        cx /= n; cz /= n;

        const outNx = new Float64Array(n);
        const outNz = new Float64Array(n);
        for (let i = 0; i < n; i++) {
            const dx = sx[i] - cx;
            const dz = sz[i] - cz;
            const len = Math.sqrt(dx * dx + dz * dz) || 1;
            outNx[i] = dx / len;
            outNz[i] = dz / len;
        }

        const ibx = new Float64Array(n);
        const ibz = new Float64Array(n);
        for (let i = 0; i < n; i++) {
            const vi = loop[i];
            ibx[i] = origPos.getX(vi) - smoothed[vi * 3]     * thickness;
            ibz[i] = origPos.getZ(vi) - smoothed[vi * 3 + 2] * thickness;
        }
        for (let iter = 0; iter < 100; iter++) {
            const tx = Float64Array.from(ibx), tz = Float64Array.from(ibz);
            for (let i = 0; i < n; i++) {
                const p = (i - 1 + n) % n, nx = (i + 1) % n;
                ibx[i] = (tx[p] + tx[i] + tx[nx]) / 3;
                ibz[i] = (tz[p] + tz[i] + tz[nx]) / 3;
            }
        }

        const BLEND_RINGS = 8;
        const smoothLevels = [20, 40, 55, 65, 75, 85, 92, 100];

        const smoothedRings = [];

        for (const iters of smoothLevels) {
            const rx = new Float64Array(n), rz = new Float64Array(n);
            const rix = new Float64Array(n), riz = new Float64Array(n);
            const ry = new Float64Array(n);
            for (let i = 0; i < n; i++) {
                const vi = loop[i];
                rx[i] = origPos.getX(vi);
                rz[i] = origPos.getZ(vi);
                rix[i] = origPos.getX(vi) - smoothed[vi * 3] * thickness;
                riz[i] = origPos.getZ(vi) - smoothed[vi * 3 + 2] * thickness;
                ry[i] = origPos.getY(vi);
            }
            for (let iter = 0; iter < iters; iter++) {
                const tx = Float64Array.from(rx), tz = Float64Array.from(rz);
                const tix = Float64Array.from(rix), tiz = Float64Array.from(riz);
                const ty = Float64Array.from(ry);
                for (let i = 0; i < n; i++) {
                    const p = (i - 1 + n) % n, nx2 = (i + 1) % n;
                    rx[i] = (tx[p] + tx[i] + tx[nx2]) / 3;
                    rz[i] = (tz[p] + tz[i] + tz[nx2]) / 3;
                    rix[i] = (tix[p] + tix[i] + tix[nx2]) / 3;
                    riz[i] = (tiz[p] + tiz[i] + tiz[nx2]) / 3;
                    ry[i] = (ty[p] + ty[i] + ty[nx2]) / 3;
                }
            }
            smoothedRings.push({ ox: rx, oz: rz, ix: rix, iz: riz, y: ry });
        }

        const outerRings = [loop.slice()];
        const innerRings = [loop.map(vi => vi + oVCount)];

        for (let r = 0; r < BLEND_RINGS; r++) {
            const t = (r + 1) / (BLEND_RINGS + 1);
            const sr = smoothedRings[r];
            const oRing = [];
            const iRing = [];
            for (let i = 0; i < n; i++) {
                const y = sr.y[i] + (topY - sr.y[i]) * t;
                oRing.push(addVert(sr.ox[i], y, sr.oz[i]));
                iRing.push(addVert(sr.ix[i], y, sr.iz[i]));
            }
            outerRings.push(oRing);
            innerRings.push(iRing);
        }

        const outerTop = [];
        const innerTop = [];
        for (let i = 0; i < n; i++) {
            outerTop.push(addVert(bx[i], topY, bz[i]));
            innerTop.push(addVert(ibx[i], topY, ibz[i]));
        }
        outerRings.push(outerTop);
        innerRings.push(innerTop);

        for (let r = 0; r < outerRings.length - 1; r++) {
            const top = outerRings[r], bot = outerRings[r + 1];
            for (let i = 0; i < n; i++) {
                const j = (i + 1) % n;
                faces.push(top[i], top[j], bot[i]);
                faces.push(bot[i], top[j], bot[j]);
            }
        }
        for (let r = 0; r < innerRings.length - 1; r++) {
            const top = innerRings[r], bot = innerRings[r + 1];
            for (let i = 0; i < n; i++) {
                const j = (i + 1) % n;
                faces.push(top[j], top[i], bot[i]);
                faces.push(top[j], bot[i], bot[j]);
            }
        }

        const borderTopY = topY + BORDER * wallDir;

        const connectRings = (upper, lower, rev = false) => {
            for (let i = 0; i < n; i++) {
                const j = (i + 1) % n;
                if (rev) {
                    faces.push(lower[j], lower[i], upper[i]);
                    faces.push(lower[j], upper[i], upper[j]);
                } else {
                    faces.push(upper[i], upper[j], lower[i]);
                    faces.push(lower[i], upper[j], lower[j]);
                }
            }
        };

        connectRings(outerTop, innerTop);

        const bdrOutBot = [];
        const bdrOutTop = [];
        const bdrInBot = [];
        const bdrInTop = [];
        for (let i = 0; i < n; i++) {
            bdrOutBot.push(addVert(bx[i] + outNx[i] * borderWidth, topY, bz[i] + outNz[i] * borderWidth));
            bdrOutTop.push(addVert(bx[i] + outNx[i] * borderWidth, borderTopY, bz[i] + outNz[i] * borderWidth));
            bdrInBot.push(addVert(ibx[i] - outNx[i] * borderWidth, topY, ibz[i] - outNz[i] * borderWidth));
            bdrInTop.push(addVert(ibx[i] - outNx[i] * borderWidth, borderTopY, ibz[i] - outNz[i] * borderWidth));
        }

        connectRings(outerTop, bdrOutBot);
        connectRings(innerTop, bdrInBot, true);
        connectRings(bdrOutBot, bdrOutTop);
        connectRings(bdrInBot, bdrInTop, true);
        connectRings(bdrOutTop, bdrInTop);
    }

    const posArr = new Float32Array(positions);
    let result = new THREE.BufferGeometry();
    result.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    result.setIndex(faces);

    result = mergeVertices(result, 1e-4);
    result = ensureConsistentNormals(result);
    result.computeBoundingBox();

    if (text && text.trim()) {
        const mainLoop = loops.reduce((a, b) => a.length >= b.length ? a : b);
        const ml = mainLoop.length;
        const smx = new Float64Array(ml);
        const smz = new Float64Array(ml);
        for (let i = 0; i < ml; i++) {
            smx[i] = origPos.getX(mainLoop[i]);
            smz[i] = origPos.getZ(mainLoop[i]);
        }
        for (let iter = 0; iter < 100; iter++) {
            const tx = Float64Array.from(smx), tz = Float64Array.from(smz);
            for (let i = 0; i < ml; i++) {
                const p = (i - 1 + ml) % ml, nx = (i + 1) % ml;
                smx[i] = (tx[p] + tx[i] + tx[nx]) / 3;
                smz[i] = (tz[p] + tz[i] + tz[nx]) / 3;
            }
        }

        let sCx = 0, sCz = 0;
        for (let i = 0; i < ml; i++) { sCx += smx[i]; sCz += smz[i]; }
        sCx /= ml; sCz /= ml;

        const arcLen = new Float64Array(ml + 1);
        for (let i = 1; i <= ml; i++) {
            const pi = i - 1, ci = i % ml;
            const dx = smx[ci] - smx[pi], dz = smz[ci] - smz[pi];
            arcLen[i] = arcLen[i - 1] + Math.sqrt(dx * dx + dz * dz);
        }
        const totalArc = arcLen[ml];

        let maxD2 = 0, frontI = 0;
        for (let i = 0; i < ml; i++) {
            const dx = smx[i] - sCx, dz = smz[i] - sCz;
            if (dx * dx + dz * dz > maxD2) { maxD2 = dx * dx + dz * dz; frontI = i; }
        }
        const frontArc = arcLen[frontI];

        const sampleCurve = (target) => {
            const t = ((target % totalArc) + totalArc) % totalArc;
            let seg = 0;
            for (let i = 1; i <= ml; i++) { if (arcLen[i] >= t) { seg = i - 1; break; } }
            const segL = arcLen[seg + 1] - arcLen[seg];
            const frac = segL > 0 ? (t - arcLen[seg]) / segL : 0;
            const i0 = seg % ml, i1 = (seg + 1) % ml;

            const px = smx[i0] + (smx[i1] - smx[i0]) * frac;
            const pz = smz[i0] + (smz[i1] - smz[i0]) * frac;

            const tdx = smx[i1] - smx[i0], tdz = smz[i1] - smz[i0];
            const tl = Math.sqrt(tdx * tdx + tdz * tdz) || 1;

            let nx = tdz / tl, nz = -tdx / tl;
            if ((px - sCx) * nx + (pz - sCz) * nz < 0) { nx = -nx; nz = -nz; }

            return { px, pz, nx, nz };
        };

        const font = _fontCache;
        if (font) {
            const wallDir2 = avgBoundaryY >= bboxCenter.y ? 1 : -1;
            const bTopY = topY + BORDER * wallDir2;
            const wallH = Math.abs(bTopY - topY);
            const textSize = Math.max(wallH * 0.7, 2);
            const textDepth = 0.8;

            const textGeo = new TextGeometry(text.trim(), {
                font, size: textSize, depth: textDepth, curveSegments: 4,
            });

            textGeo.computeBoundingBox();
            const tb = textGeo.boundingBox;
            textGeo.translate(-(tb.max.x + tb.min.x) / 2, -(tb.max.y + tb.min.y) / 2, 0);

            const wallCenterY = (topY + bTopY) / 2;
            const tPos = textGeo.getAttribute('position');

            for (let i = 0; i < tPos.count; i++) {
                const ox = tPos.getX(i);
                const oy = tPos.getY(i);
                const oz = tPos.getZ(i);

                const { px, pz, nx, nz } = sampleCurve(frontArc - ox);

                const d = thickness + oz - textDepth * 0.5;
                tPos.setXYZ(i, px + nx * d, wallCenterY + oy, pz + nz * d);
            }
            tPos.needsUpdate = true;
            textGeo.computeVertexNormals();

            let tGeo = textGeo.index ? textGeo : textGeo.toNonIndexed();
            tGeo = mergeVertices(tGeo, 1e-4);
            tGeo.computeVertexNormals();
            const tIdx2 = tGeo.getIndex();
            const tPos2 = tGeo.getAttribute('position');

            const rPos = result.getAttribute('position');
            const rIdx = result.getIndex();
            const rVCount = rPos.count;

            const combinedPos = new Float32Array((rVCount + tPos2.count) * 3);
            for (let i = 0; i < rVCount * 3; i++) combinedPos[i] = rPos.array[i];
            for (let i = 0; i < tPos2.count * 3; i++) combinedPos[rVCount * 3 + i] = tPos2.array[i];

            const combinedIdx = [];
            for (let i = 0; i < rIdx.count; i++) combinedIdx.push(rIdx.getX(i));
            for (let i = 0; i < tIdx2.count; i++) combinedIdx.push(tIdx2.getX(i) + rVCount);

            result = new THREE.BufferGeometry();
            result.setAttribute('position', new THREE.BufferAttribute(combinedPos, 3));
            result.setIndex(combinedIdx);
            result = mergeVertices(result, 1e-4);
            result.computeVertexNormals();
        }
    }

    result.computeBoundingBox();
    return result;
}
